import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useSEO, generateMedicalWebPageSchema } from '../../utils/seo';

const EmergencyPage = () => {
  // SEO Configuration
  useSEO({
    title: 'Emergency Guidance | When to Seek Immediate Medical Care for Cervical Health',
    description: 'Know when to seek immediate medical attention for cervical health concerns. Emergency symptoms, warning signs, and 24/7 cancer helpline information. National Cancer Helpline: 1800-22-1951.',
    keywords: 'cervical cancer emergency, cancer helpline India, emergency symptoms, when to see doctor, severe bleeding, cancer emergency care',
    canonical: '/emergency',
    schema: generateMedicalWebPageSchema(
      'Emergency Guidance for Cervical Health',
      'Information about when to seek immediate medical attention for cervical health concerns',
      '/emergency',
      ['Patient', 'Emergency Medical Services']
    ),
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <section className="relative h-64 flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-secondary to-red-700"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-white">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full animate-pulse-slow">
              <i className="ri-alarm-warning-line text-4xl"></i>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold font-display">
              Emergency Guidance
            </h1>
          </div>
          <p className="text-xl text-white/90">
            Know when to seek immediate medical attention
          </p>
        </div>
      </section>

      <section className="py-24 bg-gradient-to-b from-white to-red-50/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-gradient-to-br from-red-500 to-pink-600 rounded-3xl p-12 text-white text-center mb-16">
            <i className="ri-phone-line text-6xl mb-6"></i>
            <h2 className="text-3xl font-bold mb-4">National Cancer Helpline</h2>
            <div className="text-5xl font-bold mb-4">1800-22-1951</div>
            <div className="inline-block px-6 py-2 bg-white/20 rounded-full text-lg font-semibold mb-6">
              24/7 Available
            </div>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              Free helpline for cancer-related queries, guidance, and support
            </p>
            <a
              href="tel:1800221951"
              className="inline-flex items-center gap-3 px-8 py-4 bg-white text-secondary rounded-full text-lg font-semibold hover:shadow-2xl hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-fill text-xl"></i>
              Call Now
            </a>
          </div>

          <div className="mb-16">
            <h2 className="text-3xl font-bold font-display text-gray-800 mb-8 text-center">
              When to Seek <span className="text-secondary">Immediate Care</span>
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  icon: 'ri-drop-line',
                  title: 'Heavy Bleeding',
                  symptoms: [
                    'Severe vaginal bleeding',
                    'Bleeding that soaks through pads quickly',
                    'Bleeding with large clots',
                    'Bleeding causing dizziness or weakness',
                  ],
                },
                {
                  icon: 'ri-heart-pulse-line',
                  title: 'Severe Pain',
                  symptoms: [
                    'Intense pelvic or abdominal pain',
                    'Pain that prevents normal activities',
                    'Sudden sharp pain',
                    'Pain with fever or chills',
                  ],
                },
                {
                  icon: 'ri-alert-line',
                  title: 'Critical Symptoms',
                  symptoms: [
                    'Difficulty breathing',
                    'Severe weakness or fainting',
                    'High fever with bleeding',
                    'Sudden severe symptoms',
                  ],
                },
              ].map((category, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-8 shadow-lg border-l-4 border-secondary"
                >
                  <div className="w-16 h-16 flex items-center justify-center bg-secondary/10 rounded-full mb-6">
                    <i className={`${category.icon} text-3xl text-secondary`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">{category.title}</h3>
                  <ul className="space-y-3">
                    {category.symptoms.map((symptom, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-gray-700">
                        <i className="ri-alert-fill text-secondary mt-0.5 flex-shrink-0"></i>
                        <span>{symptom}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-3xl shadow-xl p-12 mb-16">
            <h2 className="text-3xl font-bold font-display text-gray-800 mb-8 text-center">
              What to Do in an <span className="text-secondary">Emergency</span>
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  step: 1,
                  title: 'Call for Help',
                  description: 'Contact emergency services (108/102) or go to the nearest hospital emergency room immediately.',
                  icon: 'ri-phone-line',
                },
                {
                  step: 2,
                  title: 'Stay Calm',
                  description: 'Try to remain calm and provide clear information about your symptoms to medical professionals.',
                  icon: 'ri-heart-pulse-line',
                },
                {
                  step: 3,
                  title: 'Bring Information',
                  description: 'If possible, bring medical records, list of medications, and any relevant health information.',
                  icon: 'ri-file-list-3-line',
                },
              ].map((step, index) => (
                <div key={index} className="text-center">
                  <div className="relative mb-6">
                    <div className="w-24 h-24 flex items-center justify-center bg-gradient-to-br from-secondary to-pink-600 rounded-full mx-auto">
                      <i className={`${step.icon} text-4xl text-white`}></i>
                    </div>
                    <div className="absolute -top-2 -right-2 w-12 h-12 flex items-center justify-center bg-white border-4 border-secondary rounded-full">
                      <span className="text-xl font-bold text-secondary">{step.step}</span>
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-3">{step.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{step.description}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-16">
            <div className="bg-gradient-to-br from-blue-50 to-teal-50 rounded-2xl p-8">
              <i className="ri-hospital-line text-4xl text-primary mb-4"></i>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Find Emergency Care</h3>
              <p className="text-gray-700 mb-6 leading-relaxed">
                Locate the nearest hospital or cancer care center with emergency services in your area.
              </p>
              <Link
                to="/hospital-directory"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-full font-semibold hover:bg-primary/90 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                Hospital Directory
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8">
              <i className="ri-question-line text-4xl text-accent mb-4"></i>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Not Sure If It's Emergency?</h3>
              <p className="text-gray-700 mb-6 leading-relaxed">
                When in doubt, it's always better to seek medical attention. Contact your healthcare provider or the cancer helpline for guidance.
              </p>
              <a
                href="tel:1800221951"
                className="inline-flex items-center gap-2 px-6 py-3 bg-accent text-white rounded-full font-semibold hover:bg-accent/90 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                Call Helpline
                <i className="ri-phone-line"></i>
              </a>
            </div>
          </div>

          <div className="bg-yellow-50 border-l-4 border-yellow-500 rounded-lg p-8">
            <div className="flex items-start gap-4">
              <i className="ri-information-line text-3xl text-yellow-600 flex-shrink-0"></i>
              <div>
                <h3 className="font-bold text-gray-800 mb-3">Important Reminders</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-yellow-600 mt-0.5 flex-shrink-0"></i>
                    <span><strong>Don't delay:</strong> If you're experiencing severe symptoms, seek immediate medical attention. Don't wait for scheduled appointments.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-yellow-600 mt-0.5 flex-shrink-0"></i>
                    <span><strong>Emergency numbers:</strong> In India, dial 108 or 102 for emergency medical services.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-yellow-600 mt-0.5 flex-shrink-0"></i>
                    <span><strong>Trust your instincts:</strong> If something feels seriously wrong, it's better to be safe and get checked.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-fill text-yellow-600 mt-0.5 flex-shrink-0"></i>
                    <span><strong>Follow-up care:</strong> After emergency treatment, ensure you follow up with your healthcare provider.</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EmergencyPage;
